import { HttpClient } from '@angular/common/http';
import { computed, Injectable, Signal } from '@angular/core';
import { BehaviorSubject, Observable, catchError, filter, map, of, shareReplay } from 'rxjs';
import { claimKeys } from './auth.const';
import { toSignal } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private readonly session$: Observable<Session> = this.httpClient.get<Session>('bff/user').pipe(
    catchError(() => {
      return of(null)
    }),
    shareReplay(1)
  );

  private user$: BehaviorSubject<UserModel> = new BehaviorSubject<UserModel>({});
  public get User() {
    return this.user$.getValue();
  }

  constructor(private readonly httpClient: HttpClient) {
    this.session$.pipe(filter(s => !!s)).subscribe(session => {
      this.user$.next({
        firstName: session?.find(x => x.type == claimKeys.firstName)?.value,
        lastName: session?.find(x => x.type == claimKeys.lastName)?.value,
        email: session?.find(x => x.type == claimKeys.email)?.value,
        sub: session?.find(x => x.type == claimKeys.sub)?.value,
        roles: session?.filter(x => x.type == claimKeys.role)?.map(x => x.value),
        publisherIds: session?.filter(x => x.type == claimKeys.publisherId)?.map(x => x.value),
      });
    });
  }

  public getUser(): Observable<UserModel> {
    return this.user$;
  }

  public getSession(): Observable<Session> {
    return this.session$;
  }

  public getIsAuthenticated(): Observable<boolean> {
    return this.getSession().pipe(
      map(session => session !== null)
    );
  }

  /** 
   * Logs the user in by redirecting to the bff. The IdP will redirect the user to the `returnUrl` after successful login.
   */
  public login(returnUrl?: string) {
    if (!returnUrl) {
      // If return url not defined, set the return to the base URL
      returnUrl = globalThis.location.origin
    }

    if (!returnUrl.startsWith(globalThis.location.origin)) {
      // If return url is a path (i.e. doesn't start with the base URL), add the base url
      returnUrl = globalThis.location.origin + returnUrl
    }
    
    globalThis.location.href = `./bff/login?returnUrl=${encodeURIComponent(returnUrl)}`;
  }

  /** Logs the user out by redirecting to the bff */
  public logout() {
    globalThis.location.href = `./bff/logout`;
  }

  public isInRole(role: string | null): Observable<boolean> {
    if (!role) {
      return of(true)
    }
    return this.getSession().pipe(
      map((session: Session) => !!session && session.some(x => x.type === claimKeys.role && x.value === role))
    );
  }

  public isInAtLeastOneRole(roles: string[] | null): Observable<boolean> {
    if (!roles || roles.length < 1) {
      return of(true);
    }

    return this.getSession().pipe(
      map((claims) => {
        //If claims are empty, user isn't in any of the roles
        if (!claims) {
          return false;
        }

        //Fetch the values of the 'role' claims from the user's session
        const tokenRoles = claims.filter(claim => claim.type == claimKeys.role)
          .map(claim => claim.value);

        return roles.some(role => tokenRoles.includes(role));
      })
    );
  }

  public UserSignal: Signal<UserModel | undefined> = toSignal(this.user$);
  public SessionSignal: Signal<Session | undefined> = toSignal(this.session$)
  public HasMultiplePublishers: Signal<boolean> = computed(() => {
    if (!this.SessionSignal()) {
      return false;
    }
    //User is admin or user has multiple publisherId claims
    return this.SessionSignal()!.some(x => x.type == claimKeys.role && x.value == "admin") ||
      this.SessionSignal()!.filter(x => x.type == claimKeys.publisherId).length > 1;
  });
}

export type Claim = {
  type: string,
  value: string
}
export type Session = Claim[] | null;

export interface UserModel {
  sub?: string,
  firstName?: string;
  lastName?: string;
  email?: string;
  publisherIds?: string[];
  roles?: string[];
}